// accessModel — pure helpers for the access-control admin console (PLEX-4095).
//
// Input is rd's GET /api/admin/access/resources payload:
//   {cell, programs: [{id, name, ownerEmail, members, projectCount}],
//          projects: [{id, name, programId, ownerEmail, members}]}
// where `members` is the resource's own list: who its owner shared it with.
//
// A program's members reach all of its projects; a project's own members are
// project-only grants (spec 2026-06-26-v2-access-control-design.md). The rows
// here follow the access engine's order: owner, then through the program, then
// a project grant.

export const OWNER = "owner";
export const PROGRAM_MEMBER = "program_member";
export const VIA_PROGRAM = "via_program";
export const PROJECT_GRANT = "project_grant";

const byName = (a, b) =>
  (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" }) ||
  String(a.id).localeCompare(String(b.id));

const lower = (email) => (email || "").trim().toLowerCase();

/**
 * Programs with their projects nested, sorted by name. Projects whose program
 * isn't in the listing (deleted, or another cell's) are returned separately,
 * so the console can still show and manage them.
 */
export const buildTree = (resources) => {
  const programs = [...(resources?.programs || [])].sort(byName);
  const ids = new Set(programs.map((p) => p.id));
  const projects = [...(resources?.projects || [])].sort(byName);
  return {
    programs: programs.map((program) => ({
      program,
      projects: projects.filter((p) => p.programId === program.id),
    })),
    unfiled: projects.filter((p) => !ids.has(p.programId)),
  };
};

/** The tree, keeping a program if its name matches or any of its projects' does. */
export const filterTree = (tree, query) => {
  const q = (query || "").trim().toLowerCase();
  if (!q) return tree;
  const hit = (r) => (r.name || "").toLowerCase().includes(q) || String(r.id).toLowerCase().includes(q);
  return {
    programs: tree.programs
      .map(({ program, projects }) => ({
        program,
        projects: hit(program) ? projects : projects.filter(hit),
      }))
      .filter(({ program, projects }) => hit(program) || projects.length > 0),
    unfiled: tree.unfiled.filter(hit),
  };
};

export const findResource = (resources, kind, id) =>
  (kind === "program" ? resources?.programs : resources?.projects)?.find((r) => r.id === id) || null;

/**
 * Who can reach this resource, one row per person:
 *   {email, badge, label, role, revocable, alsoGrant}
 * `revocable` is true only for the grant that belongs to this resource (a
 * program's member, a project's project grant). Access inherited from the
 * program is managed on the program. Someone with stronger access who is also
 * on this resource's own member list (an owner listed in their own members, or
 * a program member with a project grant too) keeps their stronger badge, and
 * that redundant grant stays revocable (`alsoGrant` names it), so it can
 * still be cleaned up. Nothing in the backend stops either case.
 */
export const accessRows = (resources, kind, id) => {
  const resource = findResource(resources, kind, id);
  if (!resource) return [];
  const rows = new Map();
  const add = (email, row) => {
    const key = lower(email);
    if (key && !rows.has(key)) rows.set(key, { email: key, role: null, revocable: false, alsoGrant: null, ...row });
  };
  // This resource's own grant, for someone who may already have a row. A row
  // that already shows a role (a program member's, via the program) keeps it;
  // an owner's row has none, so it takes the grant's.
  const ownGrant = (member, row, also) => {
    const key = lower(member.email);
    if (!key) return;
    if (rows.has(key)) {
      const existing = rows.get(key);
      rows.set(key, { ...existing, role: existing.role || member.role || null, revocable: true, alsoGrant: also });
    } else {
      add(key, { ...row, role: member.role, revocable: true });
    }
  };

  add(resource.ownerEmail, { badge: OWNER, label: "owner" });
  if (kind === "program") {
    for (const m of resource.members || []) {
      ownGrant(m, { badge: PROGRAM_MEMBER, label: "program member" }, "also program member");
    }
    return [...rows.values()];
  }

  const program = findResource(resources, "program", resource.programId);
  const via = `via ${program?.name || resource.programId}`;
  if (program) {
    add(program.ownerEmail, { badge: VIA_PROGRAM, label: via });
    for (const m of program.members || []) add(m.email, { badge: VIA_PROGRAM, label: via, role: m.role });
  }
  for (const m of resource.members || []) {
    ownGrant(m, { badge: PROJECT_GRANT, label: "project grant" }, "also project grant");
  }
  return [...rows.values()];
};

export const looksLikeEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((value || "").trim());

// ─── Person lens ──────────────────────────────────────────────────────────────
//
// Input is rd's GET /api/admin/access/users/{email}:
//   {email, plexAdmin, programs: [{id, name, ownerEmail, provenance}],
//                      projects: [{id, name, programId, ownerEmail, provenance}]}
// listing only what the user reaches; anything else in the cell is no access.

export const NO_ACCESS = "no_access";

const PERSON_LABEL = {
  owner: () => "owner",
  program_grant: () => "program member",
  via_program: (programName) => `via ${programName}`,
  project_grant: () => "project grant",
  own_chat: () => "own chat",
  [NO_ACCESS]: () => "no access",
};

const PERSON_CHIP = {
  owner: OWNER,
  program_grant: PROGRAM_MEMBER,
  via_program: VIA_PROGRAM,
  project_grant: PROJECT_GRANT,
};

export const resourceKey = (kind, id) => `${kind}:${id}`;

/** {"program:<id>" | "project:<id>": the user's entry} for a quick lookup. */
export const personIndex = (userAccess) => {
  const index = new Map();
  for (const p of userAccess?.programs || []) index.set(resourceKey("program", p.id), { ...p, kind: "program" });
  for (const p of userAccess?.projects || []) index.set(resourceKey("project", p.id), { ...p, kind: "project" });
  return index;
};

const programName = (resources, userAccess, programId) =>
  findResource(resources, "program", programId)?.name
  || (userAccess?.programs || []).find((p) => p.id === programId)?.name
  || programId;

/**
 * A tree node's badge for the person being viewed: {provenance, label, chip}.
 * A resource missing from their access is "no access".
 */
export const personBadge = (resources, userAccess, kind, id, index = personIndex(userAccess)) => {
  const entry = index.get(resourceKey(kind, id));
  const provenance = entry?.provenance || NO_ACCESS;
  const label = (PERSON_LABEL[provenance] || (() => provenance))(
    programName(resources, userAccess, entry?.programId));
  return { provenance, label, chip: PERSON_CHIP[provenance] || provenance };
};

/**
 * The user's access, one row per resource, programs first:
 *   {key, kind, id, name, programId, programName, ownerEmail, provenance, label, chip, revocable}
 * Only a grant on that very resource is revocable: a program membership on the
 * program, a project grant on the project. Access through a program goes with
 * the program's row; ownership and own chats can't be revoked.
 */
export const personRows = (resources, userAccess) => {
  const index = personIndex(userAccess);
  const rows = [];
  for (const kind of ["program", "project"]) {
    const list = [...((kind === "program" ? userAccess?.programs : userAccess?.projects) || [])].sort(byName);
    for (const r of list) {
      const badge = personBadge(resources, userAccess, kind, r.id, index);
      rows.push({
        key: resourceKey(kind, r.id),
        kind,
        id: r.id,
        name: r.name,
        programId: r.programId || null,
        programName: kind === "project" ? programName(resources, userAccess, r.programId) : null,
        ownerEmail: r.ownerEmail || null,
        ...badge,
        revocable: (kind === "program" && r.provenance === "program_grant")
          || (kind === "project" && r.provenance === "project_grant"),
      });
    }
  }
  return rows;
};

/** The cell's programs and projects the user can't reach yet: the "Grant to…" choices. */
export const grantTargets = (resources, userAccess) => {
  const index = personIndex(userAccess);
  const out = { programs: [], projects: [] };
  for (const p of [...(resources?.programs || [])].sort(byName)) {
    if (!index.has(resourceKey("program", p.id))) out.programs.push(p);
  }
  for (const p of [...(resources?.projects || [])].sort(byName)) {
    if (!index.has(resourceKey("project", p.id))) out.projects.push(p);
  }
  return out;
};
